<template>
  <div class="e-account">
    <div class="panel">
      <div class="panel-title">
        <img :src="require('@/assets/ic_seal.svg')" class="seal-icon" />
        <div class="title">电子签开通</div>
      </div>
      <xz-table :scroll="{ x: '100%', y: scrollHeight }" :columns="releaseColumns" :data="releaseData">
        <template slot="authStatus" slot-scope="_, record">
          {{ record.authStatusDesc }}
          <a-tooltip v-show="record.authStatus === 5" placement="top">
            <template slot="title">
              <span>{{ record.rejectReason }}</span>
            </template>
            <a-icon type="exclamation-circle" style="font-size: 14px; color: #8c8c8c;" />
          </a-tooltip>
        </template>
        <template slot="action" slot-scope="_, record">
          <el-button @click="handleAuth" v-if="actionPermissionMap['electronicSign:account:toAuth'] && [0, 5].includes(record.authStatus)" type="primary" size="mini">前去认证</el-button>
          <el-button @click="handleAuth" v-if="actionPermissionMap['electronicSign:account:authDetail'] && ![0, 5].includes(record.authStatus)" type="primary" size="mini">查看处理</el-button>
        </template>
      </xz-table>
    </div>
    <div class="panel">
      <div class="content-wrapper">
        <div class="h-full">
          <div id="_shopGoods" class="table-wrapper" :class="{ fullscreen: isFullScreen }">
            <xz-table
              ref="xzTable"
              :columns="columnLists"
              :data="data"
              :toolsAttrs="toolsAttrs"
              :scroll="{ x: '100%', y: scrollHeight }"
              :drawerVisible.sync="drawerVisible"
              :drawerWidth="500"
              rowKey="employeeId"
              :enableRowSelection="true"
              :selectedRowKeys="selectedKeys"
              :getCheckboxProps="(record) => ({
                props: {
                  disabled: record.status !== 0,
                },
              })"
              @change="handleSelectionChange"
              @search="handleAdvancedQuery"
              @refresh="handleReset"
              @fullScreen="handleFullScreen"
            >
              <template #buttons>
                <div class="panel-title">
                  <img :src="require('@/assets/ic_seal.svg')" class="seal-icon" />
                  <div class="title">电子签员工授权</div>
                  <el-button @click="handleChooseEmployee" type="primary" size="mini" icon="el-icon-plus" v-if="actionPermissionMap['electronicSign:account:allocationUser']">分配授权</el-button>
                  <el-button @click="handleBatchInviteRealName" :disabled="!selectedKeys.length" type="primary" size="mini" icon="el-icon-plus" plain v-if="actionPermissionMap['electronicSign:account:batchInviteUserAuth']">批量邀请认证</el-button>
                  <el-button @click="handleUpdateEmployee" type="primary" size="mini" plain v-if="actionPermissionMap['electronicSign:account:reSyncUser']"> 更新电子签员工</el-button>
                </div>
              </template>
              <template slot="action" slot-scope="_, record">
                <el-button v-if="record.status === 0 && actionPermissionMap['electronicSign:account:inviteUserAuth']" @click="handleInviteRealName(record)" type="text" size="mini">邀请实名认证</el-button>
                <el-button @click="handleDelete(record)" v-if="actionPermissionMap['electronicSign:account:deleteUser']" type="text" size="mini">删除</el-button>
              </template>
            </xz-table>
          </div>
        </div>
      </div>
      <div class="pagination-wrapper">
        <a-pagination
          :current="page.pageNum"
          :total="total"
          :show-total="total => `共 ${total} 条`"
          show-size-changer
          :pageSizeOptions="['10', '20', '30', '50', '100']"
          show-quick-jumper
          :pageSize.sync="page.pageSize"
          @change="handleNumChange"
          @showSizeChange="handleSizeChange"
        />
      </div>
    </div>

    <!-- 批量新增/更新电子签用户失败弹窗 -->
    <el-dialog
      :title="`批量${failTypeDesc}电子签用户失败`"
      :visible.sync="failedDialogVisible"
      width="600px"
      top="20vh"
    >
      <div v-for="item in failedList" :key="item.employeeId" style="padding: 2px 10px;">
        员工姓名：{{ item.name }}，失败原因：{{ item.message }}
      </div>
      <template slot="footer" class="dialog-footer">
        <el-button @click="failedDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 邀请认证生成链接弹窗 -->
    <el-dialog
      title="邀请实名认证"
      :visible.sync="loginUrlDialogVisible"
      width="800px"
      top="20vh"
    >
      <div v-for="(item, index) in loginUrlLists" :key="index" style="padding: 2px 10px 10px;">
        <div>{{ `${item.name} ${item.phoneNumber}` }}</div>
        <div>链接：{{ item.consoleUrl }}</div>
      </div>
      <template slot="footer" class="dialog-footer">
        <el-button @click="loginUrlDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <chooseEmployee ref="chooseEmployeeRef" :electSignFlag="true" @chooseEmployee="handleSelectedEmployee" />
  </div>
</template>
<script>
import * as Moment from 'dayjs';
import xzTable from '@/components/xiaozi-ui/xz-table/src/table.vue';
import chooseEmployee from '@/components/chooseEmployee.vue';
import { mapGetters } from 'vuex';
import { openElectronicSign, getElectronicSignDetail, getElectronicSignList, batchAddUser, createUrl, batchCreateUrl, deleteUser, electronicSignUserReSync } from '@/api/e-signature';
import { findAuthorization } from '@/utils/findAuthorization';
import { companyDetail } from '@/api/company';

const UNAUTHED = '1';
const AUTHING = '2';
const AUTHED = '3';

const AUDIT_ING = '1';
const AUDIT_FAILED = '3';

const RELEASE_COLUMNS = [
  {
    title: '电子签名称',
    dataIndex: 'name',
    key: 'name',
    ellipsis: true,
  },
  {
    title: '认证状态',
    dataIndex: 'authStatus',
    key: 'authStatus',
    ellipsis: true,
    scopedSlots: { customRender: 'authStatus' },
  },
  {
    title: '开通完成时间',
    dataIndex: 'openFinishTime',
    key: 'openFinishTime',
    ellipsis: true,
    customRender: text => {
      return text ? Moment(text).format('YYYY-MM-DD HH:mm:ss') : '--';
    },
  },
  {
    title: '操作',
    dataIndex: 'action',
    key: 'action',
    fixed: 'right',
    width: 200,
    scopedSlots: { customRender: 'action' },
  },
];

const COLUMN_LISTS = [
  {
    title: '员工工号',
    dataIndex: 'jobNumber',
    key: 'jobNumber',
    ellipsis: true,
  },
  {
    title: '员工姓名',
    dataIndex: 'name',
    key: 'name',
    ellipsis: true,
  },
  {
    title: '手机号',
    dataIndex: 'phoneNumber',
    key: 'phoneNumber',
    ellipsis: true,
  },
  {
    title: '电子签角色',
    dataIndex: 'roleName',
    key: 'roleName',
    ellipsis: true,
  },
  {
    title: '账号状态',
    dataIndex: 'statusDesc',
    key: 'statusDesc',
    ellipsis: true,
  },
];

export default {
  name: 'EAccount',
  components: {
    xzTable,
    chooseEmployee,
  },
  computed: {
    ...mapGetters(['actionPermissionMap']),
    toolsAttrs () {
      return {
        refresh: true,
        zoom: true,
        fullScreen: this.isFullScreen,
        // advancedSearch: true,
        // custom: true,
      };
    },
  },
  data () {
    return {
      userInfo: {},
      drawerVisible: false,
      scrollHeight: 400,
      isFullScreen: false,
      selectedKeys: [],
      releaseColumns: RELEASE_COLUMNS,
      releaseData: [],
      columnLists: [
        ...COLUMN_LISTS,
        {
          title: '操作',
          dataIndex: 'action',
          width: 150,
          key: 'action',
          fixed: 'right',
          scopedSlots: { customRender: 'action' },
        },
      ],
      data: [],
      total: 0,
      page: {
        pageNum: 1,
        pageSize: 10,
      },
      failTypeDesc: '新增',
      failedDialogVisible: false,
      failedList: [],
      loginUrlLists: [],
      loginUrlDialogVisible: false,
    };
  },
  methods: {
    handleFullScreen (flag) {
      this.isFullScreen = flag;
    },
    handleSelectionChange (keys) {
      this.selectedKeys = keys;
    },
    handleReset () {
      this.page.pageNum = 1;
      this.page.pageSize = 10;

      this.handleSearch();
    },
    handleAdvancedQuery () {
      this.page.pageNum = 1;

      this.handleSearch();
    },
    handleSizeChange (_, size) {
      this.page.pageSize = size;

      this.handleSearch();
    },
    handleNumChange (num) {
      this.page.pageNum = num;

      this.handleSearch();
    },
    async handleSearch () {
      let params = {
        ...this.outform,
        ...this.form,
        ...this.page,
        orderBy: [
          {
            orderByProperty: 'lastUpdateTime',
            orderByType: 'DESC',
          },
        ],
      };
      const { body } = await getElectronicSignList(params);
      this.data = body?.list || [];
      this.total = Number(body?.total || 0);
      if (this.drawerVisible) {
        this.drawerVisible = false;
      }
    },
    async handleAuth () {
      const res = await companyDetail({ id: this.userInfo.tenantId });
      if (res?.body?.authenticationStatus !== AUTHED) {
        let text = '';
        let showCancelButton = false;
        let cancelButtonText = '';
        let confirmButtonText = '';
        if (!findAuthorization('homePage:certification')) {
          text = '请联系管理员进行企业认证，认证审核通过后才能进行腾讯电子签认证。';
          confirmButtonText = '我知道了';
        }
        if (findAuthorization('homePage:certification')) {
          if (res?.body?.authenticationStatus === UNAUTHED && !res?.body?.authenticationAuditStatus) {
            text = '请先完成小紫平台企业认证，认证审核通过后才能进行腾讯电子签认证';
            showCancelButton = true;
            cancelButtonText = '暂不认证';
            confirmButtonText = '立即认证';
          } else if (res?.body?.authenticationStatus === UNAUTHED && res?.body?.authenticationAuditStatus === AUDIT_FAILED) {
            text = '小紫平台企业认证审核失败，请重新认证，认证审核通过后才能进行腾讯电子签认证';
            showCancelButton = true;
            cancelButtonText = '暂不认证';
            confirmButtonText = '重新认证';
          } else if (res?.body?.authenticationAuditStatus === AUDIT_ING && res?.body?.authenticationStatus === AUTHING) {
            text = '小紫平台企业认证正在审核中，审核通过后才能进行腾讯电子签认证';
            confirmButtonText = '我知道了';
          }
        }

        const h = this.$createElement;
        this.$msgbox({
          title: '',
          showClose: true,
          showCancelButton: showCancelButton,
          cancelButtonText: cancelButtonText,
          confirmButtonText: confirmButtonText,
          message: h('div', { style: 'padding: 0 20px;' }, [
            h('i', { class: 'el-icon-error', style: 'color:#FF7A75; font-size: 17px;margin-right: 10px' }),
            h('span', { style: 'font-size: 16px; font-weight: bold; color: #1F2733;' }, '电子签开通提示'),
            h('p', { style: 'padding: 5px 10px 5px 30px; font-weight: 400; font-size: 14px; color: #1F2733;' }, text),
          ]),
        }).then(() => {
          if (['立即认证', '重新认证'].includes(confirmButtonText)) {
            this.$router.push({
              name: 'IDENTIFICATION',
            });
          }
        });
        return;
      }
      const { body } = await openElectronicSign({});
      if (body?.status === 1 && body?.consoleUrl) {
        window.open(body.consoleUrl, '_blank');
      } else if (body?.status === 2) {
        this.$message.error(`生成开通链接失败：${body?.failReason || '未知错误'}`);
      }
    },
    // 分配授权
    handleChooseEmployee () {
      // let disableList = this.data.map((item) => item.employeeId) || [];
      // const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      // if (userInfo?.employeeMsg?.id) {
      //   disableList.push(userInfo.employeeMsg.id);
      // }
      this.$refs.chooseEmployeeRef.handleChooseEmployee();
    },
    handleSelectedEmployee (selectedList) {
      const employeeIds = selectedList.map(user => user.userId);
      if (!employeeIds.length) {
        this.$message.warning('您未选择员工');
        return;
      }
      this.batchAddEmployee(employeeIds);
    },
    // 批量新增用户
    async batchAddEmployee (employeeIds) {
      const { body } = await batchAddUser({
        employeeIds,
      });
      if (body?.status) {
        if ([1, '1'].includes(body.status)) {
          this.$message.success('全部新增成功');
        }
        if ([2, '2'].includes(body.status)) {
          this.failedList = body.failedList || [];
          this.failTypeDesc = '新增';
          this.failedDialogVisible = true;
        }
        setTimeout(() => {
          this.handleReset();
        }, 1000);
      }
    },
    // 更新电子签员工
    async handleUpdateEmployee () {
      const { body } = await electronicSignUserReSync({});
      if (body?.status) {
        if ([1, '1'].includes(body.status)) {
          this.$message.success('更新成功');
        }
        if ([2, '2'].includes(body.status)) {
          this.failedList = body.failedList || [];
          this.failTypeDesc = '更新';
          this.failedDialogVisible = true;
        }
        setTimeout(() => {
          this.handleReset();
        }, 1000);
      }
    },
    // 批量邀请认证
    async handleBatchInviteRealName () {
      const authStatus = this.releaseData[0]?.authStatus || 0;
      if (authStatus !== 4) {
        this.$message.warning('请等待企业电子签认证完成');
        return;
      }
      this.loginUrlLists = [];
      if (!this.selectedKeys.length) {
        this.$message.warning('请先选择员工');
        return;
      }
      const { body } = await batchCreateUrl({
        employeeIds: this.selectedKeys,
      });
      if (body?.itemList?.length) {
        this.loginUrlLists = body.itemList || [];
        this.loginUrlDialogVisible = true;
        this.handleReset();
      } else {
        this.$message.error('批量邀请实名失败');
      }
    },
    async handleInviteRealName (record) {
      const authStatus = this.releaseData[0]?.authStatus || 0;
      if (authStatus !== 4) {
        this.$message.warning('请等待企业电子签认证完成');
        return;
      }
      if (!record.employeeId) {
        this.$message.error('缺少员工ID');
        return;
      }
      const { body } = await createUrl({
        employeeId: record.employeeId,
      });
      if (!body || !body.consoleUrl) {
        this.$message.error('生成链接失败！');
        return;
      }
      this.loginUrlLists = [{
        ...record,
        consoleUrl: body.consoleUrl,
      }];
      this.loginUrlDialogVisible = true;
    },
    handleDelete (record) {
      this.$confirm(`确认删除【${record.name}, 工号：${record.jobNumber || '--'}】的电子签授权？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        deleteUser({
          employeeId: record.employeeId,
        }).then(() => {
          this.$message.success('删除成功');
          this.handleReset();
        }).catch(() => {
          this.$message.error('删除失败');
        });
      });
    },
    async queryConsoleLoginUrlDetail () {
      if (!this.userInfo.tenantId) {
        this.$message.error('请先登录');
        return;
      }
      const { body } = await getElectronicSignDetail({ tenantId: this.userInfo.tenantId });
      if (body) {
        this.releaseData = [body];
      } else {
        this.releaseData = [
          {
            authStatus: 0,
            authStatusDesc: '未认证',
            name: '腾讯电子签',
          },
        ];
      }
    },
  },
  created () {
    this.userInfo = JSON.parse(this.$local.get('userInfo')) || {};
    this.queryConsoleLoginUrlDetail();
    this.handleSearch();
  },
};
</script>
<style lang="scss" scoped>
.e-account {
  height: 100%;
  @mixin tag_round() {
    padding: 4px 8px;
    border-radius: 4px;
  }
  .panel {
    font-size: 14px;
    color: #1F2733;
    background-color: #ffffff;
    border-radius: 6px;
    padding: 15px 20px;
    margin-bottom: 8px;
    .panel-title {
      display: flex;
      align-items: center;
      .seal-icon {
        width: 22px;
        height: 22px;
      }
      .title {
        font-weight: bold;
        font-size: 14px;
        color: #333333;
        margin: 0 10px;
      }
    }
    .content-wrapper {
      flex: 1;
      overflow: hidden;
      padding: 0 0 8px;

      .h-full {
        height: 100%;
        background-color: #fff;
        display: flex;
        flex-direction: column;

        .c_group {
          position: relative;

          .title {
            font-size: 14px;
            font-weight: 600;
            padding-right: 2em;
          }
        }

        .code {
          cursor: pointer;
          color: #237FFA;
        }

        .table-wrapper {
          flex: 1;
          overflow: auto;

          .ant-radio-button-wrapper {
            border-width: 0;
            background-color: #f2f4f7;

            &.active-radio {
              border-color: #e2f3fe;
              background-color: #e2f3fe;
              color: #237ffa;
            }
          }

          .ant-radio-button-wrapper:not(:first-child)::before {
            width: 0;
          }

          .c_tag {
            @include tag_round();
            margin-right: 0.2em;
          }

          .orange {
            background-color: #fefae0;
            color: #fab007;
          }
          .red{
            background: #FEE9E5;
            color: #FF5959;
          }

          .green {
            background: #E5FEEB;
            color: #14CA64;
          }

          .grey {
            background-color: #F2F4F7;
            color: #1F2733;
          }
          .bule {
            background: #e2f3fe;
            color: #237ffa;
          }
        }
      }
    }
  }

  .table-wrapper.fullscreen {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0px;
    z-index: 100;
    background: #ffffff;
  }

  /deep/.ant-table-body::-webkit-scrollbar {
    height: 12px;
    width: 12px;
  }

  /deep/ .ant-table-body::-webkit-scrollbar-thumb {
    border-radius: 8px;
    box-shadow: inset 0 0 5px #c9ced6;
    background: #c9ced6;
  }

  //鼠标移入样式
  /deep/ .ant-table-body::-webkit-scrollbar-thumb:hover {
    background: #c9ced6;
  }

  .advanced-search {
    .ant-form-item {
      margin-bottom: 8px;
    }
  }

  /deep/.ant-drawer-body {
    padding: 16px;
  }
}
</style>
